const POWER_EPSILON = 0.0001;

const toNumber = (value) => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value !== 'string' || !value.trim()) return null;

  const normalized = value.includes(',')
    ? value.replace(/\./g, '').replace(',', '.')
    : value;
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
};

export const getOptionPower = (option) => toNumber(option?.potenciaParsed);

export const getOptionModules = (option) => {
  const modules = toNumber(option?.quantidadeModulos);
  return modules == null ? 0 : Math.trunc(modules);
};

export const normalizeInverterModel = (value) => String(value || '')
  .toUpperCase()
  .replace(/[^A-Z0-9]/g, '');

export const parseInverterModels = (value) => {
  const values = Array.isArray(value) ? value : [value];
  const models = values
    .flatMap(item => String(item || '').split(/[,;\n]+/))
    .map(item => item.trim())
    .filter(Boolean);

  return models.filter((model, index) => (
    models.findIndex(candidate => (
      normalizeInverterModel(candidate) === normalizeInverterModel(model)
    )) === index
  ));
};

const collectStringValues = (value, output = [], seen = new Set()) => {
  if (typeof value === 'string') {
    output.push(value);
    return output;
  }
  if (!value || typeof value !== 'object' || seen.has(value)) return output;

  seen.add(value);
  if (Array.isArray(value)) {
    value.forEach(item => collectStringValues(item, output, seen));
  } else {
    Object.values(value).forEach(item => collectStringValues(item, output, seen));
  }
  return output;
};

export const optionUsesInverter = (option, inverterModels) => {
  const targets = parseInverterModels(inverterModels)
    .map(normalizeInverterModel)
    .filter(Boolean);
  if (!targets.length) return false;

  const searchableValues = collectStringValues(option).map(normalizeInverterModel);
  return targets.some(target => (
    searchableValues.some(value => value.includes(target))
  ));
};

export const getMaxPowerForInverters = (options, inverterModels) => {
  const matchingPowers = options
    .filter(option => optionUsesInverter(option, inverterModels))
    .map(getOptionPower)
    .filter(power => power != null);

  return matchingPowers.length ? Math.max(...matchingPowers) : null;
};

const getOptionPrice = (option) => toNumber(option?.valorFinal) ?? Infinity;

export const pickBestOption = (options, targetPower, {
  targetModules = null,
  maxModules = Infinity,
  maxPower = Infinity,
} = {}) => {
  let eligible = options.filter((option) => {
    const modules = getOptionModules(option);
    const power = getOptionPower(option);
    return modules <= maxModules
      && power != null
      && power <= maxPower + POWER_EPSILON;
  });

  if (!eligible.length) return null;

  if (targetModules != null) {
    const exactModules = eligible.filter(option => getOptionModules(option) === targetModules);
    if (exactModules.length) {
      eligible = exactModules;
    } else {
      const nextModuleCount = Math.min(
        ...eligible
          .map(getOptionModules)
          .filter(modules => modules >= targetModules)
      );
      if (Number.isFinite(nextModuleCount)) {
        eligible = eligible.filter(option => getOptionModules(option) === nextModuleCount);
      } else {
        return null;
      }
    }
  }

  const atOrAboveTarget = eligible.filter(option => (
    getOptionPower(option) >= targetPower - POWER_EPSILON
  ));
  let candidates = atOrAboveTarget;
  if (!candidates.length) {
    const highestAvailablePower = Math.max(...eligible.map(getOptionPower));
    candidates = eligible.filter(option => (
      Math.abs(getOptionPower(option) - highestAvailablePower) <= POWER_EPSILON
    ));
  }

  return candidates.reduce((best, option) => (
    getOptionPrice(best) <= getOptionPrice(option) ? best : option
  ));
};
